import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  PreloadingStrategy,
  Route,
  Router,
} from '@angular/router';
import { Observable, of } from 'rxjs';

const SLOW_CONNECTION_TYPES: ReadonlySet<string> = new Set([
  'slow-2g',
  '2g',
  '3g',
]);

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

/**
 * Preloads the lazy routes of the section the user is currently in, so moving
 * between its pages doesn't wait on a chunk download.
 *
 * A section opts in with `data: { preloadChildren: true }`; its `children` are
 * preloaded only while one of them is active. Code for other sections is never
 * fetched, and nothing is preloaded under Save-Data or a 2G/3G connection.
 */
@Injectable({ providedIn: 'root' })
export class ActiveSectionPreloadingStrategy implements PreloadingStrategy {
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!this.isBrowser || this.isConstrainedNetwork()) return of(null);
    return this.activeSectionChildren().has(route) ? load() : of(null);
  }

  private activeSectionChildren(): ReadonlySet<Route> {
    const children = new Set<Route>();
    let snapshot: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (snapshot) {
      const config = snapshot.routeConfig;
      if (config?.data?.['preloadChildren'] === true) {
        for (const child of config.children ?? []) children.add(child);
      }
      snapshot = snapshot.firstChild;
    }
    return children;
  }

  private isConstrainedNetwork(): boolean {
    const connection = (
      navigator as Navigator & { connection?: NetworkInformationLike }
    ).connection;
    if (!connection) return false;
    return (
      connection.saveData === true ||
      SLOW_CONNECTION_TYPES.has(connection.effectiveType ?? '')
    );
  }
}
